import { useRef, useState } from 'react';
import { useRouter } from 'next/router';

import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

import { AlbumFunnelModalService } from '@/components/pages/albums/AlbumFunnelModal/services/AlbumFunnelModalService';

import useTranslation from '@/utils/hooks/translation/useTranslation';

dayjs.extend(utc);
dayjs.extend(timezone);

// Hooks
import { PREVIOUS_STEP, useAlbumContext } from '@/utils/hooks/context/useAlbumContext';
import { useGroupLabels } from '@/utils/hooks/swr/useGroupLabels';
import useHashtags from '@/utils/hooks/swr/useHashtags';
import useLabels from '@/utils/hooks/swr/useLabels';
import useUser from '@/utils/hooks/swr/useUser';
import useActiveGroup from '@/utils/hooks/user/useActiveGroup';
import useActiveSocialProfile from '@/utils/hooks/user/useActiveSocialProfile';
import useIsGroup from '@/utils/hooks/user/useIsGroup';

import useIntersectionObserver from '@/utils/hooks/utils/useIntersectionObserver';

// Lib
import { CONTENT_ALBUM_ANALYTICS } from '@/lib/routes/privateRoutes';

import Badge from '@/components/base/Badge';
import Icon from '@/components/base/Icon';
import InfoBanner from '@/components/base/InfoBanner';
import Modal from '@/components/base/Modal';
import { AlbumTagsFilter } from '@/components/pages/albums/AlbumFunnelModal/ModalSteps/AutomaticStep/AlbumTagsFilter';
import DatepickerFilter from '@/components/pages/albums/AlbumFunnelModal/ModalSteps/AutomaticStep/DatepickerFilter';
import HashtagsFilter from '@/components/pages/albums/AlbumFunnelModal/ModalSteps/AutomaticStep/HashtagsFilter';
import PlaceholderLoader from '@/components/pages/albums/AlbumFunnelModal/ModalSteps/AutomaticStep/PlaceholderLoader';
import PostTypeFilter from '@/components/pages/albums/AlbumFunnelModal/ModalSteps/AutomaticStep/PostTypeFilter';
import { SocialProfilesFilter } from '@/components/pages/albums/AlbumFunnelModal/ModalSteps/AutomaticStep/SocialProfilesFilter';
import FooterModal from '@/components/pages/albums/AlbumFunnelModal/components/FooterModal';
import { InputAlbumName } from '@/components/pages/albums/AlbumFunnelModal/components/InputAlbumName';
import InstagramMediaType from '@/components/pages/albums/AlbumFunnelModal/components/InstagramMediaType';

export const AutomaticStep = ({ dismiss, callbackUpdateAlbum }) => {
    const router = useRouter();
    const refBannerInfo = useRef();
    const bannerInfoInViewPort = useIntersectionObserver(refBannerInfo, '0px');
    const [submitStatus, setSubmitStatus] = useState({
        isSubmitting: false,
        error: null
    });
    const [socialProfilesError, setSocialProfilesError] = useState(null);

    const { user } = useUser();
    const { albumInfo, dispatch } = useAlbumContext();
    const isGroup = useIsGroup();
    const { t } = useTranslation('content');
    const { activeSocialProfile } = useActiveSocialProfile();
    const { activeGroup } = useActiveGroup();

    const { hashtags, isLoading: isLoadingHashtags } = useHashtags({
        activeSPID: activeSocialProfile?.id,
        options: { revalidateOnFocus: false, revalidateOnReconnect: false },
        shouldFetch: !isGroup
    });

    const { isLoadingInitialData: isLoadingSocialProfileLabelsInitialData } = useLabels({
        activeSPID: activeSocialProfile?.id,
        options: { revalidateOnFocus: false, revalidateOnReconnect: false },
        shouldFetch: !isGroup
    });
    const { isLoadingInitialData: isLoadingGroupLabelsInitialData } = useGroupLabels({
        activeGroupID: activeGroup?.id,
        options: { revalidateOnFocus: false, revalidateOnReconnect: false },
        shouldFetch: isGroup
    });

    const isLoadingLabelsInitialData = isGroup
        ? isLoadingGroupLabelsInitialData
        : isLoadingSocialProfileLabelsInitialData;

    const submitAutomaticAlbum = async () => {
        setSocialProfilesError(null);
        setSubmitStatus((prevState) => ({
            ...prevState,
            isSubmitting: true,
            error: null
        }));

        const result = await AlbumFunnelModalService.submitAutomaticAlbum({
            albumInfo,
            isGroup,
            activeSocialProfileId: activeSocialProfile?.id,
            activeGroupId: activeGroup?.id,
            userTimezone: user?.settings?.timezone
        });

        if (!result.success) {
            if (result.error === 'social_profiles_required') {
                setSocialProfilesError(t('content:modal.social_profiles_required_error'));
                setSubmitStatus((prevState) => ({
                    ...prevState,
                    isSubmitting: false,
                    error: null
                }));
                return;
            }

            const errorMessage =
                result.error === 'unique_name'
                    ? t('content:modal.update_album_error_name_unique')
                    : t('errors:error_occured_submit_data');

            setSubmitStatus((prevState) => ({
                ...prevState,
                isSubmitting: false,
                error: errorMessage
            }));

            if (!bannerInfoInViewPort) {
                refBannerInfo?.current?.scrollIntoView({ behavior: 'smooth' });
            }
            return;
        }

        if (albumInfo?.id) {
            if (typeof callbackUpdateAlbum === 'function') {
                await callbackUpdateAlbum();
            }

            if (typeof dismiss === 'function') {
                dismiss();
            }
        } else if (result.albumId) {
            router.push(CONTENT_ALBUM_ANALYTICS.replace('[albumId]', result.albumId));
        }
    };

    return (
        <div className="w-[calc(100vw-24px)] sm:max-w-[600px] md:max-w-[800px] lg:max-w-[1000px]">
            <Modal.Body>
                <div ref={refBannerInfo} />
                {submitStatus?.error ? (
                    <InfoBanner
                        className="mb-4"
                        title={t('errors:error')}
                        message={submitStatus?.error}
                        onClose={() => {
                            setSubmitStatus((prevState) => ({
                                ...prevState,
                                error: null
                            }));
                        }}
                    />
                ) : null}
                <div className="mb-8 flex items-center gap-3">
                    {!albumInfo?.id && (
                        <button
                            onClick={() => dispatch({ type: PREVIOUS_STEP })}
                            disabled={submitStatus?.isSubmitting}
                            className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full border border-icoGray-300 text-icoGray-500 transition-colors hover:bg-icoGray-100 dark:border-icoDarkMode-beton dark:text-icoDarkMode-wolf dark:hover:bg-icoDarkMode-deepBlack"
                            aria-label={t('commons:back')}>
                            <Icon icon="arrow-left" className="text-xs" />
                        </button>
                    )}

                    <Modal.Title>{albumInfo?.name}</Modal.Title>

                    <Badge variant="modal_campaign_type" iconSize="sm">
                        {t('content:modal.create_modal.action.automatic')}
                    </Badge>
                </div>

                {!!albumInfo?.id && <InputAlbumName className="mb-6" />}

                <Modal.Content className="rounded-md">
                    {isLoadingHashtags || isLoadingLabelsInitialData ? (
                        <PlaceholderLoader />
                    ) : (
                        <div>
                            <InstagramMediaType />

                            <h3 className="mb-5 mt-5 font-GSemiBold text-xl leading-[25px] tracking-[-0.5px] text-icoBlack dark:text-white">
                                {t('modal.campaign_settings')}
                            </h3>

                            <div className="flex flex-col gap-5 rounded-md bg-icoGray-100 p-5 dark:bg-icoDarkMode-deepBlack sm:p-8 md:flex-row">
                                <div className="flex-1 space-y-5">
                                    <PostTypeFilter />
                                    <HashtagsFilter hashtags={hashtags} />
                                    <DatepickerFilter />
                                </div>

                                <div className="flex-1 space-y-5">
                                    {isGroup && (
                                        <SocialProfilesFilter
                                            error={socialProfilesError}
                                            onClearError={() => setSocialProfilesError(null)}
                                        />
                                    )}

                                    <AlbumTagsFilter />
                                </div>
                            </div>
                        </div>
                    )}
                </Modal.Content>
            </Modal.Body>
            <FooterModal
                disableSubmitButton={
                    isLoadingHashtags ||
                    isLoadingLabelsInitialData ||
                    socialProfilesError ||
                    (isGroup && !albumInfo?.criteria?.social_profiles.length)
                }
                submitStatus={submitStatus}
                submitAlbum={submitAutomaticAlbum}
                onCancel={dismiss}
            />
        </div>
    );
};
